// Teams theme → `data-theme` on <html>.
//
// The 2026-07-28 design review (finding 2) caught the SPA shipping
// `<meta name="color-scheme" content="light dark">` with nothing behind it:
// on a dark Teams the browser painted its own dark canvas and dark form
// chrome while every colour in the page stayed a light-mode literal. This
// module is the "behind it" — it reads the host's theme once at boot and
// again on every change, and sets one attribute. The palette swap itself is
// pure CSS (`src/styles/tailwind.css` → `:root[data-theme="dark"]`), so no
// component knows a theme exists.
//
// Teams reports three themes: `default`, `dark`, `contrast`. High contrast
// maps onto the dark ground here and is then handled by the `forced-colors`
// block in the stylesheet — Windows overrides the palette itself in that
// mode, so the job is to not fight it.

import { getThemeAndSubscribe } from "./teams";

export type ThemeName = "light" | "dark";

/** Map a Teams theme string onto the two grounds the token map defines. */
export function normaliseTeamsTheme(theme: string | null | undefined): ThemeName {
  return theme === "dark" || theme === "contrast" ? "dark" : "light";
}

/** Ground chosen when there is no Teams host to ask (local dev, Playwright,
 * a browser tab opened straight at the SPA). */
export function systemTheme(): ThemeName {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

/** Write the ground onto <html>. Idempotent; safe to call from a handler. */
export function applyTheme(theme: ThemeName): void {
  if (typeof document === "undefined") return;
  document.documentElement.dataset.theme = theme;
}

/** Read the Teams theme, apply it, and keep applying it on change.
 *
 * Resolves once the initial ground is on the element, and returns a
 * disposer for the OS-level listener installed in the no-host case. Any
 * failure degrades to the system preference rather than throwing — a theme
 * is never worth failing a boot over. */
export async function startThemeSync(): Promise<() => void> {
  const media =
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(prefers-color-scheme: dark)")
      : null;
  const onSystemChange = (): void => applyTheme(systemTheme());

  let hostTheme: string | null = null;
  try {
    hostTheme = await getThemeAndSubscribe((theme) => {
      applyTheme(normaliseTeamsTheme(theme));
    });
  } catch {
    hostTheme = null;
  }

  if (hostTheme !== null) {
    // Inside Teams the host owns the theme; the OS media query would fight
    // it (a light Teams on a dark OS is a real, common combination).
    applyTheme(normaliseTeamsTheme(hostTheme));
    return () => {};
  }

  applyTheme(systemTheme());
  media?.addEventListener("change", onSystemChange);
  return () => media?.removeEventListener("change", onSystemChange);
}
